import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency } from '@/lib/utils';

interface PendingStrategy {
  id: number;
  name: string;
  creator_id: number;
  creator_name: string;
  backtest_months: number;
  backtest_return: number;
  backtest_win_rate: number;
  price: number;
  plan_type: string;
  description: string;
  tags: string;
}

export function StrategiesPage() {
  const qc = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const { data: strategies = [], isLoading } = useQuery<PendingStrategy[]>({
    queryKey: ['admin', 'strategies', 'pending'],
    queryFn: () => adminClient.get('/admin/strategies/pending').then((r) => r.data),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, note }: { id: number; action: 'approve' | 'reject'; note?: string }) =>
      adminClient.post(`/admin/strategies/${id}/review`, { action, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'strategies'] });
      setRejectOpen(false);
      setRejectNote('');
      setRejectId(null);
    },
  });

  const openReject = (id: number) => {
    setRejectId(id);
    setRejectNote('');
    setRejectOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">策略審核</h1>
        <Badge variant={strategies.length > 0 ? 'warning' : 'secondary'}>
          {strategies.length} 筆待審
        </Badge>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : strategies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-500" />
            <p>目前沒有待審核的策略</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>策略名稱</TableHead>
                  <TableHead>創建者</TableHead>
                  <TableHead>回測月數</TableHead>
                  <TableHead>回報率</TableHead>
                  <TableHead>勝率</TableHead>
                  <TableHead>訂閱價格</TableHead>
                  <TableHead>方案門檻</TableHead>
                  <TableHead className="w-[120px]">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {strategies.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{s.description}</p>
                    </TableCell>
                    <TableCell className="text-sm">{s.creator_name}</TableCell>
                    <TableCell className="text-sm">{s.backtest_months} 個月</TableCell>
                    <TableCell>
                      <span className={s.backtest_return >= 0 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
                        {s.backtest_return >= 0 ? '+' : ''}{s.backtest_return.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{s.backtest_win_rate.toFixed(1)}%</TableCell>
                    <TableCell className="text-sm">{formatCurrency(s.price)} / 月</TableCell>
                    <TableCell><Badge variant="outline">{s.plan_type}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => reviewMutation.mutate({ id: s.id, action: 'approve' })}
                          disabled={reviewMutation.isPending}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />通過
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive border-destructive/20 hover:bg-destructive/5"
                          onClick={() => openReject(s.id)}
                          disabled={reviewMutation.isPending}
                        >
                          <XCircle className="w-4 h-4 mr-1" />拒絕
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>拒絕策略</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <label className="text-sm font-medium">拒絕原因（必填）</label>
            <Input
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="請說明拒絕原因..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>取消</Button>
            <Button
              variant="destructive"
              disabled={!rejectNote.trim() || reviewMutation.isPending}
              onClick={() => rejectId && reviewMutation.mutate({ id: rejectId, action: 'reject', note: rejectNote })}
            >
              {reviewMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              確認拒絕
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
